import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useRef, useState } from 'react'

import {
  InvitePermissionOption,
  InvitePersonalAccount
} from '@/api/models/users/lib/invites.types'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { AppInput } from '@/components/shared/input'
import { useOutsideClick } from '@/hooks/useOutsideClick'

import { MobilePremisePermissionsPicker } from './MobilePremisePermissionsPicker'
import { PremisePermissionsPicker } from './PremisePermissionsPicker'

type Props = {
  options: InvitePersonalAccount['permissions']
  selected: InvitePermissionOption[]
  onChange: (selectedPermission: InvitePermissionOption[]) => void
  premiseName: string
  projectName: string
}

export const PremisePermissionsDropdown = ({
  options,
  selected,
  onChange,
  premiseName,
  projectName
}: Props) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)

  const onClose = () => {
    setIsOpen(false)
  }

  const handleTogglePicker = () => {
    isOpen ? onClose() : setIsOpen(true)
  }

  const isFullAccessSelected =
    options.manualSelection.length > 0 &&
    options.manualSelection.every(option =>
      selected.some(item => item.alias === option.alias)
    )

  const getSelectorText = () => {
    if (selected.length === 0) {
      return ''
    }
    if (isFullAccessSelected) {
      return 'Полный доступ'
    }
    return 'Частичный доступ'
  }

  useOutsideClick({
    ref: wrapperRef,
    onClose,
    isOpen,
    disabled: isMobileScreen
  })

  return (
    <div ref={wrapperRef} className="relative">
      <AppInput
        type="text"
        dataTestId="premise-permissions-access-input-id"
        inputClassName="cursor-pointer"
        autoComplete="off"
        className={cn(
          'w-full border',
          isOpen ? 'border-bronze-40' : 'border-grey-20'
        )}
        readOnly
        onClick={handleTogglePicker}
        value={getSelectorText()}
        placeholder="Доступ"
        extraContent={
          <ChevronDownIcon
            className={cn(
              'h-12 w-12 text-grey-60',
              isOpen ? 'rotate-180' : undefined
            )}
          />
        }
      />
      {isOpen && !isMobileScreen && (
        <div className="z-10 -mt-[5px]">
          <div
            className={cn(
              'absolute z-[60] mt-8 flex w-[360px] flex-col gap-8 rounded-12 border border-grey-20 bg-white p-12 xxs:p-0',
              'xxs:relative xxs:h-full xxs:justify-between xxs:border-0'
            )}
          >
            <PremisePermissionsPicker
              onChange={onChange}
              selected={selected}
              options={options}
              isFullAccessSelected={isFullAccessSelected}
            />
          </div>
        </div>
      )}

      {isOpen && isMobileScreen && (
        <MobilePremisePermissionsPicker
          onClose={() => setIsOpen(false)}
          placeholder="Доступы"
          isOpen={isOpen}
          premiseName={premiseName}
          projectName={projectName}
        >
          <PremisePermissionsPicker
            onChange={onChange}
            selected={selected}
            options={options}
            isFullAccessSelected={isFullAccessSelected}
          />
        </MobilePremisePermissionsPicker>
      )}
    </div>
  )
}
